/**
 * WordPress dependencies
 */
import { PanelBody, TextControl, TextareaControl, ToggleControl, SelectControl, RadioControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
const { HeaderTabs, ColorControl, BackgroundControl, BorderControl, ResBoxControl, TypographyControl, IconPickerControl, ResRangeControl } =
    window.faqmateModules;

// global constants
const { HEADING_TAGS } = window.faqmateModules.GlobalConstants;

// obj attributes
import objAttributes from './attributes';

// constants
import {
    C_BG,
    C_BORDER,
    C_BRADIUS,
    C_MARGIN,
    C_PADDING,
    T_TYPO,
    T_MARGIN,
    Q_TYPO,
    Q_MARGIN,
    I_BORDER,
    I_BRADIUS,
    I_PADDING,
    I_SIZE,
    I_BG
} from './constants';

const Inspector = props => {
    const { attributes, setAttributes } = props;
    const {
        // faq
        items,

        icon,
        sourceType,
        titleColor,
        answerColor,
        iconColor,
        showIcon,
        countColor
    } = attributes;
    const { enableAutoNumbering = false, numberingType = 'numeric', numberingPosition = 'before' } = attributes;

    const objAttrs = { attributes, setAttributes, objAttributes };

    return (
        <HeaderTabs
            generalControls={
                <>
                    <PanelBody title={__('Faq', 'faqmate-blocks')} initialOpen={false}>
                        {Array.isArray(items) &&
                            items.map((item, index) => {
                                return (
                                    <div key={index}>
                                        <TextControl
                                            label={__('Question', 'faqmate-blocks')}
                                            value={item?.question || ''}
                                            onChange={v => {
                                                const newItems = [...items];
                                                if (newItems[index]) {
                                                    newItems[index].question = v;
                                                    setAttributes({
                                                        items: newItems
                                                    });
                                                }
                                            }}
                                            placeholder={__('Question', 'faqmate-blocks')}
                                        />
                                        <TextareaControl
                                            label={__('Answer', 'faqmate-blocks')}
                                            value={item?.answer || ''}
                                            onChange={v => {
                                                const newItems = [...items];
                                                if (newItems[index]) {
                                                    newItems[index].answer = v;
                                                    setAttributes({
                                                        items: newItems
                                                    });
                                                }
                                            }}
                                            placeholder={__('Answer', 'faqmate-blocks')}
                                        />
                                    </div>
                                );
                            })}
                        <button
                            className="esab__add-accordion"
                            onClick={() => {
                                const newItems = [...(items || [])];
                                newItems.push({
                                    id: newItems.length.toString(),
                                    question: 'New Question',
                                    answer: 'New Answer'
                                });
                                setAttributes({
                                    items: newItems
                                });
                            }}
                        >
                            {__('Add Accordion', 'faqmate-block')}
                        </button>
                    </PanelBody>
                    {showIcon && (
                        <PanelBody title={__('Icon', 'faqmate-blocks')} initialOpen={false}>
                            <ToggleControl
                                label={__('Show Icon', 'faqmate-blocks')}
                                checked={showIcon}
                                onChange={value => setAttributes({ showIcon: value })}
                            />
                            {sourceType === 'library' && (
                                <IconPickerControl
                                    label={__('Select Icon', 'faqmate-blocks')}
                                    value={icon}
                                    onChange={v => {
                                        setAttributes({
                                            icon: v
                                        });
                                    }}
                                />
                            )}
                        </PanelBody>
                    )}
                    <PanelBody title={__('Auto Numbering', 'accordion')} initialOpen={false}>
                        <ToggleControl
                            label={__('Enable Auto Numbering', 'accordion')}
                            checked={enableAutoNumbering}
                            onChange={value => setAttributes({ enableAutoNumbering: value })}
                            help={__('Automatically add numbers/prefixes to FAQ items', 'accordion')}
                        />

                        {enableAutoNumbering && (
                            <>
                                <SelectControl
                                    label={__('Numbering Style', 'accordion')}
                                    value={numberingType}
                                    options={[
                                        { label: '1. 2. 3. (Numeric)', value: 'numeric' },
                                        { label: 'Q1. Q2. Q3. (Question)', value: 'question' },
                                        { label: 'A. B. C. (Uppercase)', value: 'alpha-upper' },
                                        { label: 'a. b. c. (Lowercase)', value: 'alpha-lower' },
                                        { label: 'I. II. III. (Roman Upper)', value: 'roman-upper' },
                                        { label: 'i. ii. iii. (Roman Lower)', value: 'roman-lower' },
                                        { label: '[1] [2] [3] (Bracket Numeric)', value: 'bracket-numeric' },
                                        { label: '[Q1] [Q2] [Q3] (Bracket Question)', value: 'bracket-question' }
                                    ]}
                                    onChange={value => setAttributes({ numberingType: value })}
                                />

                                <RadioControl
                                    label={__('Numbering Position', 'accordion')}
                                    selected={numberingPosition}
                                    labelPosition="side"
                                    options={[
                                        { label: 'Before Question', value: 'before' },
                                        { label: 'After Question', value: 'after' }
                                    ]}
                                    onChange={value => setAttributes({ numberingPosition: value })}
                                />
                            </>
                        )}
                    </PanelBody>
                </>
            }
            styleControls={
                <>
                    <PanelBody title={__('Container', 'faqmate-blocks')} initialOpen={false}>
                        <BorderControl objAttrs={objAttrs} controlName={C_BORDER} noHover={true} />
                        <ResBoxControl label={__('Border Radius', 'faqmate-blocks')} objAttrs={objAttrs} controlName={C_BRADIUS} />
                        <ResBoxControl label={__('Padding', 'faqmate-blocks')} objAttrs={objAttrs} controlName={C_PADDING} />
                        <ResBoxControl label={__('Margin', 'faqmate-blocks')} objAttrs={objAttrs} controlName={C_MARGIN} />
                        <BackgroundControl label={__('Background', 'faqmate-blocks')} objAttrs={objAttrs} controlName={C_BG} />
                    </PanelBody>

                    <PanelBody title={__('Question', 'faqmate-blocks')} initialOpen={false}>
                        <ColorControl
                            label={__('Color', 'faqmate-blocks')}
                            color={titleColor}
                            onChange={v => {
                                setAttributes({
                                    titleColor: v
                                });
                            }}
                        />
                        <TypographyControl label={__('Typography', 'faqmate-blocks')} objAttrs={objAttrs} controlName={T_TYPO} />
                        <ResBoxControl label={__('Margin', 'faqmate-blocks')} objAttrs={objAttrs} controlName={T_MARGIN} />
                    </PanelBody>
                    <PanelBody title={__('Answer', 'faqmate-blocks')} initialOpen={false}>
                        <ColorControl
                            label={__('Color', 'faqmate-blocks')}
                            color={answerColor}
                            onChange={v => {
                                setAttributes({
                                    answerColor: v
                                });
                            }}
                        />
                        <TypographyControl label={__('Typography', 'faqmate-blocks')} objAttrs={objAttrs} controlName={Q_TYPO} />
                        <ResBoxControl label={__('Margin', 'faqmate-blocks')} objAttrs={objAttrs} controlName={Q_MARGIN} />
                    </PanelBody>
                    {showIcon && (
                        <PanelBody title={__('Icon', 'faqmate-blocks')} initialOpen={false}>
                            <ResRangeControl
                                label={__('Size', 'faqmate-blocks')}
                                controlName={I_SIZE}
                                objAttrs={objAttrs}
                                min={1}
                                max={100}
                            />
                            <BorderControl objAttrs={objAttrs} controlName={I_BORDER} />
                            <ResBoxControl label={__('Border Radius', 'faqmate-blocks')} controlName={I_BRADIUS} objAttrs={objAttrs} />
                            <ResBoxControl label={__('Padding', 'faqmate-blocks')} controlName={I_PADDING} objAttrs={objAttrs} />
                            <ColorControl
                                label={__('Color', 'faqmate-blocks')}
                                color={iconColor}
                                onChange={v => {
                                    setAttributes({
                                        iconColor: v
                                    });
                                }}
                            />
                            <BackgroundControl label={__('Background', 'faqmate-blocks')} objAttrs={objAttrs} controlName={I_BG} />
                        </PanelBody>
                    )}
                    {enableAutoNumbering && (
                        <PanelBody title={__('Number Prefix', 'faqmate-blocks')} initialOpen={false}>
                            <ColorControl
                                label={__('Color', 'faqmate-blocks')}
                                color={countColor}
                                onChange={v => {
                                    setAttributes({
                                        countColor: v
                                    });
                                }}
                            />
                        </PanelBody>
                    )}
                </>
            }
        />
    );
};

export default Inspector;
